import React, { useMemo, useState } from "react";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Handle, Position,
  MarkerType, useNodesState, useEdgesState, useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "@dagrejs/dagre";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, {
  formatApiError, CRITICALITY, STATUS, EDGE_TYPES, RESILIENCE_FILTERS, RESILIENCE_FILTER_KEYS,
  tCrit, tStatus,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, AlertTriangle, Server, Boxes, Building2, Layers, X, ArrowUpRight,
  ArrowDownLeft, ExternalLink, Waypoints, GitBranch, Crosshair, ArrowLeft,
} from "lucide-react";

const TYPE_ICON = { asset: Server, service: Boxes, organization: Building2 };

// Node footprints (kept in sync with dagre so layout has no overlaps).
const ASSET_W = 224, ASSET_H = 86;
const SVC_W = 248, SVC_H = 104;

// ---------------------------------------------------------------------------
// Custom node — readable, criticality-coded, service vs asset differentiated.
// ---------------------------------------------------------------------------
function CrosNode({ data, selected }) {
  const isSvc = data.ntype === "service";
  const Icon = TYPE_ICON[data.ntype] || Server;
  const crit = CRITICALITY[data.criticality] || {};
  const dot = crit.dot || "#71717A";
  const resil = data.resilience ? RESILIENCE_FILTERS[data.resilience] : null;
  const fds = data.failure_domains || [];
  const critical = data.criticality === "critical" || data.criticality === "high";
  const ringCls = selected
    ? "ring-2 ring-sky-400"
    : data.inCycle
      ? "ring-2 ring-orange-500"
      : "";
  return (
    <div
      className={`rounded-md ${ringCls} transition-shadow`}
      style={{
        width: isSvc ? SVC_W : ASSET_W,
        minHeight: isSvc ? SVC_H : ASSET_H,
        background: isSvc ? "#16161c" : "#111114",
        border: `${isSvc ? 2 : 1.5}px solid ${dot}${isSvc ? "" : "88"}`,
        boxShadow: isSvc ? `0 0 0 1px ${dot}33, 0 6px 20px -8px ${dot}55` : "0 4px 14px -8px #000",
        padding: "10px 12px",
      }}
      data-testid={`graph-node-${data.id}`}
    >
      <Handle type="target" position={Position.Top} style={{ background: "#52525B", width: 8, height: 8 }} />
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: dot }} />
        <Icon className={`h-4 w-4 shrink-0 ${isSvc ? "text-white" : "text-muted-foreground"}`} />
        <span className="text-sm font-semibold text-white leading-tight" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {data.label}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <span
          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
          style={{ background: isSvc ? dot + "22" : "#27272A", color: isSvc ? dot : "#A1A1AA" }}
        >
          {isSvc ? (critical ? "Service critique" : "Service") : "Actif"}
        </span>
        <span className="text-[10px] font-medium" style={{ color: dot }}>{tCrit(data.criticality)}</span>
      </div>
      {resil && (
        <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] px-1.5 py-0.5 rounded-sm"
          style={{ background: resil.dot + "1f", color: resil.dot }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: resil.dot }} />
          {resil.label}
        </div>
      )}
      {fds.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1 text-[9px] text-yellow-400/90" title={fds.map((d) => d.name).join(", ")}
          style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <Layers className="h-2.5 w-2.5 shrink-0" />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fds.map((d) => d.name).join(", ")}</span>
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={{ background: "#52525B", width: 8, height: 8 }} />
    </div>
  );
}

const nodeTypes = { cros: CrosNode };

// ---------------------------------------------------------------------------
// Hierarchical layout with dagre (top-down). Services float to the TOP by
// reversing service-incoming edges for both ranking and rendering, so ALL
// rendered edges flow downward (source above → target below) with clean routing.
// dagre also breaks cycles internally, so the graph stays compact & overlap-free.
// ---------------------------------------------------------------------------
function buildLayout(rawNodes, rawEdges) {
  const typeOf = {};
  rawNodes.forEach((n) => (typeOf[n.id] = n.type));

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", nodesep: 70, ranksep: 110, marginx: 40, marginy: 40 });
  g.setDefaultEdgeLabel(() => ({}));
  rawNodes.forEach((n) => {
    const isSvc = n.type === "service";
    g.setNode(n.id, { width: isSvc ? SVC_W : ASSET_W, height: isSvc ? SVC_H : ASSET_H });
  });

  const outEdges = rawEdges.map((e) => {
    const reversed = typeOf[e.target] === "service"; // put the service on top
    return {
      ...e,
      origSource: e.source,
      origTarget: e.target,
      source: reversed ? e.target : e.source,
      target: reversed ? e.source : e.target,
      reversed,
    };
  });
  outEdges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);

  const nodes = rawNodes.map((n) => {
    const p = g.node(n.id);
    const isSvc = n.type === "service";
    const w = isSvc ? SVC_W : ASSET_W, h = isSvc ? SVC_H : ASSET_H;
    return {
      id: n.id,
      type: "cros",
      position: { x: (p?.x ?? 0) - w / 2, y: (p?.y ?? 0) - h / 2 },
      data: {
        id: n.id, label: n.name, ntype: n.type, criticality: n.criticality,
        status: n.status, resilience: n.resilience, failure_domains: n.failure_domains || [],
      },
    };
  });
  return { nodes, layoutEdges: outEdges };
}

// ---------------------------------------------------------------------------
// Canvas — isolated so it can drive fitView through useReactFlow.
// ---------------------------------------------------------------------------
function GraphCanvas({ nodes, edges, onNodesChange, onEdgesChange, fitSignature, onNodeClick, onNodeDoubleClick, onPaneClick }) {
  const rf = useReactFlow();
  React.useEffect(() => {
    if (!nodes.length) return;
    const t = setTimeout(() => rf.fitView({ padding: 0.22, maxZoom: 1.3, duration: 450 }), 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitSignature]);
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={onNodeClick}
      onNodeDoubleClick={onNodeDoubleClick}
      onPaneClick={onPaneClick}
      nodeTypes={nodeTypes}
      fitView
      fitViewOptions={{ padding: 0.22, maxZoom: 1.3 }}
      minZoom={0.25}
      maxZoom={2}
      nodesDraggable
      proOptions={{ hideAttribution: true }}
    >
      <Background color="#27272A" gap={24} />
      <Controls className="!bg-card" />
      <MiniMap
        nodeColor={(n) => CRITICALITY[n.data?.criticality]?.dot || "#71717A"}
        maskColor="#09090Bcc"
        style={{ background: "#121214", border: "1px solid #27272A" }}
        pannable zoomable
      />
    </ReactFlow>
  );
}

// ---------------------------------------------------------------------------
// Selection details panel (right drawer).
// ---------------------------------------------------------------------------
function NodePanel({ node, detail, reasons, watch, canFocus, onFocus, onClose, onOpen }) {
  if (!node) return null;
  const isSvc = node.ntype === "service";
  const crit = CRITICALITY[node.criticality] || {};
  const st = STATUS[node.status] || {};
  const resil = node.resilience ? RESILIENCE_FILTERS[node.resilience] : null;
  const incoming = detail?.incoming || [];
  const outgoing = detail?.outgoing || [];
  return (
    <div className="absolute top-0 right-0 h-full w-[340px] bg-[#0d0d10] border-l border-border z-10 flex flex-col"
      data-testid="node-panel">
      <div className="flex items-start justify-between gap-2 p-4 border-b border-border">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {isSvc ? <Boxes className="h-4 w-4 text-white shrink-0" /> : <Server className="h-4 w-4 text-muted-foreground shrink-0" />}
            <span className="text-sm font-semibold text-white truncate" data-testid="node-panel-name">{node.label}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-300">
              {isSvc ? (node.criticality === "critical" || node.criticality === "high" ? "Service critique" : "Service") : "Actif"}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${crit.cls || ""}`}>{tCrit(node.criticality)}</span>
            {node.status && <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${st.cls || ""}`}>{tStatus(node.status)}</span>}
          </div>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-white shrink-0" data-testid="node-panel-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {resil && (
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm"
              style={{ background: resil.dot + "1f", color: resil.dot }} data-testid="node-panel-resilience">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: resil.dot }} />
              {resil.label}
            </div>
          </div>
        )}

        {(reasons?.length > 0 || watch) && (
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5">Pourquoi ?</div>
            <div className="space-y-2" data-testid="node-panel-why">
              {reasons?.map((r, i) => (
                <div key={i} className="text-xs text-zinc-300 bg-zinc-900/60 rounded-sm p-2 border border-border">
                  <span className="text-[10px] font-medium" style={{ color: RESILIENCE_FILTERS[r.category]?.dot }}>
                    {RESILIENCE_FILTERS[r.category]?.label} · {r.service_name}
                  </span>
                  <p className="mt-1 leading-relaxed text-zinc-400">{r.reason}</p>
                </div>
              ))}
              {watch && (watch.criteria || []).map((c, i) => (
                <div key={`w${i}`} className="text-xs text-zinc-400 bg-zinc-900/60 rounded-sm p-2 border border-border">{c}</div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <ArrowDownLeft className="h-3 w-3" /> Relations entrantes ({incoming.length})
          </div>
          {incoming.length === 0 && <p className="text-xs text-muted-foreground">Aucune.</p>}
          <div className="space-y-1">
            {incoming.map((r) => (
              <div key={r.edge_id} className="text-xs flex items-center gap-1.5">
                <span className="px-1 py-0.5 rounded-sm text-[10px]" style={{ background: (EDGE_TYPES[r.type]?.color || "#71717A") + "22", color: EDGE_TYPES[r.type]?.color || "#A1A1AA" }}>{r.type_label}</span>
                <span className="text-zinc-300 truncate">{r.node?.name}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <ArrowUpRight className="h-3 w-3" /> Relations sortantes ({outgoing.length})
          </div>
          {outgoing.length === 0 && <p className="text-xs text-muted-foreground">Aucune.</p>}
          <div className="space-y-1">
            {outgoing.map((r) => (
              <div key={r.edge_id} className="text-xs flex items-center gap-1.5">
                <span className="px-1 py-0.5 rounded-sm text-[10px]" style={{ background: (EDGE_TYPES[r.type]?.color || "#71717A") + "22", color: EDGE_TYPES[r.type]?.color || "#A1A1AA" }}>{r.type_label}</span>
                <span className="text-zinc-300 truncate">{r.node?.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {node.ntype !== "organization" && (
        <div className="p-3 border-t border-border space-y-2">
          {canFocus && (
            <Button size="sm" variant="outline" className="w-full rounded-sm gap-2" onClick={onFocus} data-testid="node-panel-focus">
              <Crosshair className="h-3.5 w-3.5" /> Focus sur ce service
            </Button>
          )}
          <Button size="sm" className="w-full rounded-sm gap-2" onClick={onOpen} data-testid="node-panel-open">
            <ExternalLink className="h-3.5 w-3.5" /> Ouvrir la fiche
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function Dependencies() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [filterCrit, setFilterCrit] = useState("all");
  const [selectedTypes, setSelectedTypes] = useState(() => Object.keys(EDGE_TYPES));
  const toggleType = (t) => setSelectedTypes((s) => s.includes(t) ? s.filter((x) => x !== t) : [...s, t]);
  const [selectedResil, setSelectedResil] = useState(() => [...RESILIENCE_FILTER_KEYS]);
  const toggleResil = (r) => setSelectedResil((s) => s.includes(r) ? s.filter((x) => x !== r) : [...s, r]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ source_id: "", target_id: "", type: "depends_on", criticality: "medium", description: "" });
  const [selected, setSelected] = useState(null);          // {id, ntype, ...data}
  const [showCycles, setShowCycles] = useState(false);
  // --- Focus Service (sous-graphe contextuel) ---
  const [searchParams, setSearchParams] = useSearchParams();
  const [focusId, setFocusId] = useState(() => searchParams.get("focus") || null);
  const [depth, setDepth] = useState(2);
  const enterFocus = (sid) => { setSelected(null); setShowCycles(false); setFocusId(sid); setSearchParams({ focus: sid }); };
  const exitFocus = () => { setSelected(null); setFocusId(null); setSearchParams({}); };

  const { data: graph } = useQuery({ queryKey: ["graph"], queryFn: async () => (await api.get("/graph")).data });
  const { data: cycles } = useQuery({ queryKey: ["cycles"], queryFn: async () => (await api.get("/dependencies/cycles")).data });
  const { data: resilience } = useQuery({ queryKey: ["dashboard", "resilience"], queryFn: async () => (await api.get("/dashboard/resilience")).data });

  // Focus Service: sous-graphe contextuel borné en profondeur (moteur via /impact).
  const { data: impact } = useQuery({
    queryKey: ["service-impact-focus", focusId, depth],
    enabled: !!focusId,
    queryFn: async () => (await api.get(`/services/${focusId}/impact`, { params: { depth } })).data,
  });

  // Engine justifications indexed by asset & the "services to watch" entry per service.
  const reasonsByAsset = useMemo(() => {
    const m = {};
    const el = resilience?.elements || {};
    Object.entries(el).forEach(([category, arr]) => {
      (arr || []).forEach((e) => {
        (m[e.asset_id] ||= []).push({ category, service_name: e.service_name, reason: e.reason });
      });
    });
    return m;
  }, [resilience]);
  const watchByService = useMemo(() => {
    const m = {};
    (resilience?.services_to_watch || []).forEach((w) => (m[w.service_id] = w));
    return m;
  }, [resilience]);

  // Justifications CONTEXTUELLES au service focalisé (source: moteur via /impact.direct).
  // Un même actif peut être SPOF pour un service et redondance partielle pour un autre :
  // en Focus on n'affiche QUE la qualification du service courant.
  const focusReasons = useMemo(() => {
    const m = {};
    (impact?.direct || []).forEach((d) => {
      if (!RESILIENCE_FILTER_KEYS.includes(d.resilience_filter)) return;
      m[d.id] = [{ category: d.resilience_filter, service_name: impact?.service?.name, reason: d.spof_reason }];
    });
    return m;
  }, [impact]);

  // Detail (incoming/outgoing) for the currently selected node.
  const { data: selDetail } = useQuery({
    queryKey: ["node-detail", selected?.ntype, selected?.id],
    enabled: !!selected && selected.ntype !== "organization",
    queryFn: async () => {
      const base = selected.ntype === "service" ? "services" : "assets";
      return (await api.get(`/${base}/${selected.id}/detail`)).data;
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["graph"] });
    qc.invalidateQueries({ queryKey: ["cycles"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };
  const create = useMutation({
    mutationFn: async () => (await api.post("/dependencies", form)).data,
    onSuccess: (res) => {
      toast.success("Dépendance créée");
      if (res.cycle_warning) toast.warning("⚠ Cycle de dépendances détecté");
      setOpen(false);
      invalidate();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  // Cycle highlight sets (reuse the API's existing detection — no new engine).
  const { cycleNodeIds, cyclePairs } = useMemo(() => {
    const nodeIds = new Set();
    const pairs = new Set();
    if (showCycles && cycles?.cycles) {
      cycles.cycles.forEach((path) => {
        for (let i = 0; i < path.length; i++) {
          nodeIds.add(path[i]);
          if (i < path.length - 1) pairs.add(`${path[i]}->${path[i + 1]}`);
        }
      });
    }
    return { cycleNodeIds: nodeIds, cyclePairs: pairs };
  }, [showCycles, cycles]);

  const fitSignature = useMemo(
    () => `${focusId || "global"}|${depth}|${filterCrit}|${selectedTypes.join(",")}|${selectedResil.join(",")}|${(focusId ? impact?.graph?.nodes?.length : graph?.nodes?.length) || 0}`,
    [focusId, depth, filterCrit, selectedTypes, selectedResil, graph, impact]
  );

  React.useEffect(() => {
    const inFocus = !!focusId;
    let rawNodes, rawEdges;
    if (inFocus) {
      if (!impact?.graph) return;
      // Sous-graphe contextuel au service (moteur via /impact). La qualification de
      // résilience de chaque actif est celle DE CE SERVICE (resilience_filter), jamais
      // la pire globale. Indirects/alternatives non qualifiés → pas de badge.
      rawNodes = impact.graph.nodes.map((n) => ({
        id: n.id, name: n.name, type: n.type, criticality: n.criticality, status: n.status,
        resilience: n.type === "asset" ? (n.resilience_filter || null) : null,
        failure_domains: n.failure_domains || [],
      }));
      rawEdges = impact.graph.edges;
    } else {
      if (!graph) return;
      rawNodes = graph.nodes; rawEdges = graph.edges;
    }
    const allResil = selectedResil.length === RESILIENCE_FILTER_KEYS.length;
    const nodeResilOk = (n) => n.type !== "asset" || !n.resilience || allResil || selectedResil.includes(n.resilience);
    const filtered = (filterCrit === "all" ? rawNodes : rawNodes.filter((n) => n.criticality === filterCrit)).filter(nodeResilOk);
    const ids = new Set(filtered.map((n) => n.id));
    const visibleEdges = rawEdges.filter((e) => ids.has(e.source) && ids.has(e.target) && selectedTypes.includes(e.type));

    const { nodes: laidOut, layoutEdges } = buildLayout(filtered, visibleEdges);
    setNodes(laidOut.map((n) => ({ ...n, data: { ...n.data, inCycle: cycleNodeIds.has(n.id) } })));
    setEdges(
      layoutEdges.map((e) => {
        const inCycle = cyclePairs.has(`${e.origSource}->${e.origTarget}`);
        const color = inCycle ? "#FF9F0A" : (EDGE_TYPES[e.type]?.color || "#71717A");
        return {
          id: e.id, source: e.source, target: e.target, label: e.type_label,
          markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
          animated: inCycle || e.criticality === "critical",
          style: {
            stroke: color,
            strokeWidth: inCycle ? 2.75 : (e.criticality === "critical" ? 2.25 : 1.75),
            strokeDasharray: e.is_alternative ? "6 4" : undefined,
          },
          labelStyle: { fill: "#D4D4D8", fontSize: 10, fontFamily: "JetBrains Mono" },
          labelBgStyle: { fill: "#09090B", fillOpacity: 0.85 },
          labelBgPadding: [4, 2], labelBgBorderRadius: 3, labelShowBg: true,
        };
      })
    );
  }, [graph, impact, focusId, filterCrit, selectedTypes, selectedResil, cycleNodeIds, cyclePairs, setNodes, setEdges]);

  const nodeOptions = graph?.nodes || [];
  const writable = can("dependency:write");

  const onNodeClick = (_e, n) => setSelected({ ...n.data });
  const onNodeDoubleClick = (_e, n) => {
    if (n.data.ntype === "service") navigate(`/services/${n.data.id}`);
    else if (n.data.ntype === "asset") navigate(`/assets/${n.data.id}`);
  };
  const openSelected = () => {
    if (!selected) return;
    if (selected.ntype === "service") navigate(`/services/${selected.id}`);
    else if (selected.ntype === "asset") navigate(`/assets/${selected.id}`);
  };

  return (
    <AppShell title="Cartographie des dépendances" subtitle="Graphe hiérarchique Service → Actifs → Dépendances"
      actions={
        <div className="flex items-center gap-2">
          <Select value={filterCrit} onValueChange={setFilterCrit}>
            <SelectTrigger className="w-40 rounded-sm h-9" data-testid="graph-filter-criticality"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes criticités</SelectItem>
              <SelectItem value="critical">Critique</SelectItem>
              <SelectItem value="high">Élevé</SelectItem>
              <SelectItem value="medium">Moyen</SelectItem>
              <SelectItem value="low">Faible</SelectItem>
            </SelectContent>
          </Select>
          {writable && (
            <Button size="sm" className="rounded-sm gap-2" onClick={() => setOpen(true)} data-testid="create-dependency-button">
              <Plus className="h-4 w-4" /> Dépendance
            </Button>
          )}
        </div>
      }>
      {/* Filtre multi-sélection par type de relation */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="type-filter">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mr-1">Type de relation :</span>
        {Object.entries(EDGE_TYPES).map(([k, v]) => {
          const on = selectedTypes.includes(k);
          return (
            <button key={k} onClick={() => toggleType(k)} data-testid={`type-chip-${k}`}
              className={`px-2.5 py-1 rounded-sm border text-xs transition-colors ${on ? "text-white" : "text-muted-foreground opacity-50"}`}
              style={{ borderColor: v.color + "80", background: on ? v.color + "22" : "transparent" }}>
              {v.label}
            </button>
          );
        })}
        <button onClick={() => setSelectedTypes(Object.keys(EDGE_TYPES))} className="text-xs text-primary ml-1" data-testid="type-chip-all">Tout</button>
      </div>

      {/* Filtre par état de résilience (source unique = moteur) */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="resilience-filter">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground mr-1">Résilience :</span>
        {RESILIENCE_FILTER_KEYS.map((k) => {
          const on = selectedResil.includes(k);
          const v = RESILIENCE_FILTERS[k];
          return (
            <button key={k} onClick={() => toggleResil(k)} data-testid={`resil-chip-${k}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs transition-colors ${on ? "text-white" : "text-muted-foreground opacity-50"}`}
              style={{ borderColor: v.dot + "80", background: on ? v.dot + "22" : "transparent" }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: v.dot }} /> {v.label}
            </button>
          );
        })}
        <button onClick={() => setSelectedResil([...RESILIENCE_FILTER_KEYS])} className="text-xs text-primary ml-1" data-testid="resil-chip-all">Tout</button>
      </div>

      {cycles?.has_cycles && !focusId && (
        <div className="flex items-center justify-between gap-2 mb-3 p-3 rounded-sm border border-orange-500/40 bg-orange-500/10 text-sm text-orange-400" data-testid="cycle-warning">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {cycles.count} cycle(s) de dépendances détecté(s) — avertissement non bloquant.
          </span>
          <Button size="sm" variant="outline"
            className={`rounded-sm h-7 gap-1.5 border-orange-500/50 ${showCycles ? "bg-orange-500/20 text-orange-300" : "text-orange-400"}`}
            onClick={() => setShowCycles((s) => !s)} data-testid="view-cycles-button">
            <GitBranch className="h-3.5 w-3.5" /> {showCycles ? "Masquer les cycles" : "Voir les cycles"}
          </Button>
        </div>
      )}

      {focusId && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 p-3 rounded-sm border border-sky-500/40 bg-sky-500/10" data-testid="focus-bar">
          <div className="flex items-center gap-2 text-sm text-sky-300">
            <Crosshair className="h-4 w-4" />
            <span className="font-semibold" data-testid="focus-service-name">Focus : {impact?.service?.name || "…"}</span>
            <span className="text-xs text-sky-400/70">sous-graphe contextuel (profondeur {depth})</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Profondeur :</span>
              <Select value={String(depth)} onValueChange={(v) => setDepth(Number(v))}>
                <SelectTrigger className="w-28 rounded-sm h-8" data-testid="focus-depth-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 niveau</SelectItem>
                  <SelectItem value="2">2 niveaux</SelectItem>
                  <SelectItem value="3">3 niveaux</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="outline" className="rounded-sm h-8 gap-1.5" onClick={exitFocus} data-testid="exit-focus-button">
              <ArrowLeft className="h-3.5 w-3.5" /> Revenir à la vue globale
            </Button>
          </div>
        </div>
      )}

      <div className="relative border border-border rounded-sm bg-[#0b0b0d] overflow-hidden" style={{ height: "calc(100vh - 250px)", minHeight: 480 }} data-testid="dependency-graph">
        <ReactFlowProvider>
          <GraphCanvas
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            fitSignature={fitSignature}
            onNodeClick={onNodeClick} onNodeDoubleClick={onNodeDoubleClick}
            onPaneClick={() => setSelected(null)}
          />
        </ReactFlowProvider>

        {/* Légende discrète */}
        <div className="absolute top-3 left-3 z-10 bg-[#0d0d10]/90 border border-border rounded-sm p-2.5 text-[10px] space-y-1.5 backdrop-blur-sm" data-testid="graph-legend">
          <div className="font-mono uppercase tracking-wider text-muted-foreground mb-1">Légende</div>
          <div className="flex items-center gap-1.5 text-zinc-300"><span className="inline-block h-3 w-4 rounded-sm border-2 border-red-500 bg-[#16161c]" /> Service critique</div>
          <div className="flex items-center gap-1.5 text-zinc-300"><span className="inline-block h-3 w-4 rounded-sm border border-blue-500/60 bg-[#111114]" /> Actif</div>
          <div className="h-px bg-border my-1" />
          {RESILIENCE_FILTER_KEYS.map((k) => (
            <div key={k} className="flex items-center gap-1.5 text-zinc-400">
              <span className="h-2 w-2 rounded-full" style={{ background: RESILIENCE_FILTERS[k].dot }} /> {RESILIENCE_FILTERS[k].label}
            </div>
          ))}
        </div>

        <NodePanel
          node={selected}
          detail={selDetail}
          reasons={selected?.ntype === "asset" ? (focusId ? focusReasons[selected.id] : reasonsByAsset[selected.id]) : null}
          watch={selected?.ntype === "service" && !focusId ? watchByService[selected.id] : null}
          canFocus={!!selected && selected.ntype === "service" && !focusId}
          onFocus={() => selected && enterFocus(selected.id)}
          onClose={() => setSelected(null)}
          onOpen={openSelected}
        />
      </div>

      {/* Légende des types de relation */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-3 text-xs">
        {Object.entries(EDGE_TYPES).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5 text-muted-foreground">
            <span className="h-0.5 w-5" style={{ background: v.color }} /> {v.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span className="h-0.5 w-5 border-t border-dashed border-zinc-400" /> Relation alternative
        </span>
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Layers className="h-3 w-3 text-yellow-400" /> Domaine de défaillance
        </span>
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <Waypoints className="h-3 w-3" /> Clic : détails · Double-clic : fiche
        </span>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="dependency-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Nouvelle dépendance</DialogTitle>
            <DialogDescription>Edge typé entre deux nodes du graphe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={form.source_id} onValueChange={(v) => setForm({ ...form, source_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="dep-source-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {nodeOptions.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Type de relation</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(EDGE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cible</Label>
              <Select value={form.target_id} onValueChange={(v) => setForm({ ...form, target_id: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="dep-target-select"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {nodeOptions.filter((n) => n.id !== form.source_id).map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Criticité</Label>
              <Select value={form.criticality} onValueChange={(v) => setForm({ ...form, criticality: v })}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critique</SelectItem>
                  <SelectItem value="high">Élevé</SelectItem>
                  <SelectItem value="medium">Moyen</SelectItem>
                  <SelectItem value="low">Faible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => create.mutate()} disabled={!form.source_id || !form.target_id || create.isPending} data-testid="save-dependency-button">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
